import { useState, useRef, useMemo } from 'react';
import './versionlog.css';
import { api } from '../../api/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { ViewToggle } from '../../components/ViewToggle';
import { CheckedCombo } from '../../components/CheckedCombo';
import { AiCombo } from '../../components/AiCombo';
import { useFetchMs } from '../../hooks/useFetchMs';
import { useAuthStore } from '../../auth/store';
import { serverMessage } from '../../components/confirm/serverMessage';
import { useModalKeys } from '../../hooks/useModalKeys';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore — Vite ?url suffix gives us a local asset URL for the worker
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// ── Types ────────────────────────────────────────────────────────────────────

interface VersionLogListItem {
  oid: string;
  versionNumber: string | null;
  xafVersionNumber: string | null;
  dateReleased: string | null;
  releasedBy: string | null;
  dateTested: string | null;
  testedBy: string | null;
  ticketId: string | null;
  attachment: string | null;
}

interface VersionPostItem {
  oid: string;
  ticketId: string | null;
  category: number;
  categoryLabel: string | null;
  comment: string | null;
  subject: string | null;
  moduleName: string | null;
  formName: string | null;
  author: string | null;
  postedDateTime: string | null;
}

interface VersionLogDetail extends VersionLogListItem {
  description: string | null;
  posts: VersionPostItem[];
}

interface VersionLogInput {
  versionNumber: string;
  xafVersionNumber: string;
  description: string;
  dateReleased: string;
  releasedBy: string;
  dateTested: string;
  testedBy: string;
  ticketId: string;
}

interface VersionPostInput {
  ticketId: string;
  category: number;
  comment: string;
  subject: string;
  moduleName: string;
  formName: string;
}

/** One (version, post) pair for the Version Tracking report. */
interface VersionTrackingItem {
  versionNumber: string | null;
  dateReleased: string | null;
  versionTicketId: string | null;
  taskTicketId: string | null;
  category: number;
  categoryLabel: string | null;
  moduleName: string | null;
  formName: string | null;
  comment: string | null;
}

// ── API ──────────────────────────────────────────────────────────────────────

const vlApi = {
  list: () => api.get<{ success: boolean; data: VersionLogListItem[] }>('/api/v1/version-log'),
  tracking: () => api.get<{ success: boolean; data: VersionTrackingItem[] }>('/api/v1/version-log/tracking'),
  getById: (id: string) =>
    api.get<{ success: boolean; data: VersionLogDetail }>(`/api/v1/version-log/${id}`),
  create: (input: VersionLogInput) =>
    api.post<{ success: boolean; data: { id: string } }>('/api/v1/version-log', input),
  update: (id: string, input: VersionLogInput) =>
    api.put<{ success: boolean }>(`/api/v1/version-log/${id}`, input),
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/api/v1/version-log/${id}`),
  addPost: (id: string, input: VersionPostInput) =>
    api.post<{ success: boolean; data: { id: string } }>(`/api/v1/version-log/${id}/posts`, input),
  deletePost: (id: string, postId: string) =>
    api.delete<{ success: boolean }>(`/api/v1/version-log/${id}/posts/${postId}`),
  uploadAttachment: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ success: boolean; data: { path: string; fileName: string } }>(
      `/api/v1/version-log/${id}/attachment`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  downloadAttachment: (id: string) =>
    api.get(`/api/v1/version-log/${id}/attachment`, { responseType: 'blob' }),
};

const CATEGORY_OPTIONS = [
  { value: 0, label: 'New Feature' },
  { value: 1, label: 'Enhancement' },
  { value: 2, label: 'Bug Fixing' },
];

const EMPTY_INPUT: VersionLogInput = {
  versionNumber: '',
  xafVersionNumber: '',
  description: '',
  dateReleased: '',
  releasedBy: '',
  dateTested: '',
  testedBy: '',
  ticketId: '',
};

const EMPTY_POST: VersionPostInput = { ticketId: '', category: 0, comment: '', subject: '', moduleName: '', formName: '' };

/** A blank detail record so a NEW form can hold PDF-parsed Posts before the
 *  first save (the Posts grid reads `detail.posts`). */
const EMPTY_DETAIL: VersionLogDetail = {
  oid: '', versionNumber: null, xafVersionNumber: null, dateReleased: null,
  releasedBy: null, dateTested: null, testedBy: null, ticketId: null,
  attachment: null, description: null, posts: [],
};

/** Convert a PDF date like "10/26/2026" (MM/DD/YYYY) or "2026-10-26" to the
 *  yyyy-MM-dd value an <input type="date"> needs; '' when unrecognised. */
function pdfDateToIso(s: string): string {
  const t = s.replace(/\s+/g, '');            // pdf.js may split "10 / 26 / 2026"
  const mdy = t.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdy) return `${mdy[3]!}-${mdy[1]!.padStart(2, '0')}-${mdy[2]!.padStart(2, '0')}`;
  const iso = t.match(/(\d{4})-(\d{2})-(\d{2})/);
  return iso ? iso[0] : '';
}

/** 'list' = landing grid; 'view' = read-only detail (row click); 'edit' =
 *  editable existing record; 'new' = editable blank record. */
type DetailMode = 'list' | 'view' | 'edit' | 'new';

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Maintenance → Security → Version Log.
 *
 * List View → (row click = view · Edit = edit · New) → full-page **Detail View**
 * (XAF `VersionControl_DetailView` parity, BO UI Standard §4 / standing rule 12),
 * matching the shared `<SettingsDetailPage>` chrome used by every Settings BO:
 * the List View / Detail View switch (`<ViewToggle>`), the `.sd-head` title +
 * breadcrumb + prev/next record arrows, a **GENERAL INFORMATION** section, the
 * XAF two-column side-label fields, the **Posts** child grid, and the action bar
 * rendered TOP and BOTTOM — **View mode = Edit · Close**, **Edit mode = Save ·
 * Save and Close · Save and New · Cancel**.
 */
export default function VersionLogPage() {
  const qc = useQueryClient();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  // G5 (VersionCreationViewController): only the Service user can New / Edit / Delete / Save a version — everyone else reads.
  // The API enforces the same rule; this only keeps the buttons honest.
  const canWrite = useAuthStore((s) => (s.user?.userName ?? '').trim().toLowerCase() === 'service');
  // Released By is the Deployment Team lookup (G5 ReleasedBy → dbo.DeploymentTeam).
  const { data: teamData } = useQuery({
    queryKey: ['version-log-released-by'],
    queryFn: () => api.get<{ data?: string[] }>('/api/v1/version-log/released-by-options').then(r => r.data.data ?? []),
  });
  const teamNames = teamData ?? [];

  // list data
  const { data: listData, isFetching } = useQuery({
    queryKey: ['version-log'],
    queryFn: () => vlApi.list().then(r => r.data.data),
  });
  const loadMs = useFetchMs(isFetching);
  const rows = listData ?? [];

  // search
  const [search, setSearch] = useState('');
  const term = search.toLowerCase().trim();
  const filteredRows = term
    ? rows.filter(r =>
        (r.versionNumber ?? '').toLowerCase().includes(term) ||
        (r.xafVersionNumber ?? '').toLowerCase().includes(term) ||
        (r.releasedBy ?? '').toLowerCase().includes(term) ||
        (r.testedBy ?? '').toLowerCase().includes(term) ||
        (r.ticketId ?? '').toLowerCase().includes(term))
    : rows;

  const handleSearch = (v: string) => setSearch(v);

  // shared-grid selection (checkbox column + row click)
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const effectiveSelectedId = selectedIds.size === 1 ? [...selectedIds][0] as string : null;
  const canEdit = selectedIds.size === 1;
  const canDelete = selectedIds.size > 0;

  // grid's own column-filtered + sorted set (pre-pagination) for "Export to"
  const [visibleRows, setVisibleRows] = useState<VersionLogListItem[]>(filteredRows);

  // ── Detail / mode state ─────────────────────────────────────────────────────
  const [mode, setMode] = useState<DetailMode>('list');
  const [editId, setEditId] = useState<string | null>(null); // null = New
  const [form, setForm] = useState<VersionLogInput>(EMPTY_INPUT);
  const [detail, setDetail] = useState<VersionLogDetail | null>(null);
  const [errors, setErrors] = useState<Partial<VersionLogInput>>({});

  const viewOnly = mode === 'view';

  // add-post dialog
  const [postDlgOpen, setPostDlgOpen] = useState(false);
  const [postForm, setPostForm] = useState<VersionPostInput>(EMPTY_POST);

  // attachment / PDF state
  const [attachFileName, setAttachFileName] = useState('');
  const [pdfParsing, setPdfParsing] = useState(false);
  // The browsed PDF is HELD here and uploaded on Save (after the record exists),
  // so Browse works on a NEW record too — the upload endpoint needs a saved id.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Version Tracking (cross-version Posts report) — filter by Module / Form.
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingRows, setTrackingRows] = useState<VersionTrackingItem[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  // Multi-select: an empty array means "all". Picking several modules/forms
  // shows every version/task that touched ANY of them (OR within each box, AND
  // between the two boxes).
  const [moduleFilter, setModuleFilter] = useState<string[]>([]);
  const [formFilter, setFormFilter] = useState<string[]>([]);

  // Prev/next record navigation — walks the current (search-filtered) list order.
  const siblingIds = useMemo(() => filteredRows.map(r => r.oid), [filteredRows]);
  const curIdx = editId ? siblingIds.indexOf(editId) : -1;
  const prevId = curIdx > 0 ? siblingIds[curIdx - 1] : null;
  const nextId = curIdx >= 0 && curIdx < siblingIds.length - 1 ? siblingIds[curIdx + 1] : null;

  // Parse PDF and extract posts — one post per table row (Tracker ID + Description)
  const handlePdfFile = async (file: File) => {
    setAttachFileName(file.name);
    setPdfParsing(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      // Collect each visual line as its x-sorted cells — keep the x so the table
      // COLUMNS can be told apart (No. | Tracker ID | Description | Module Name |
      // Form Name), not flattened into one string.
      type Cell = { x: number; str: string };
      const pageLines: Cell[][] = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const content = await page.getTextContent();

        const lineMap = new Map<number, Cell[]>();
        for (const item of content.items as any[]) {
          const str = (item.str ?? '').trim();
          if (!str) continue;
          const y = Math.round(item.transform[5]);
          if (!lineMap.has(y)) lineMap.set(y, []);
          lineMap.get(y)!.push({ x: item.transform[4], str });
        }

        const sortedYs = [...lineMap.keys()].sort((a, b) => b - a);
        for (const y of sortedYs) pageLines.push(lineMap.get(y)!.sort((a, b) => a.x - b.x));
      }

      // Learn the x-position of EACH table column from the "No. | Tracker ID |
      // … | Description" header row. The order of Module / Form / Description
      // VARIES between PDF layouts, so we bucket data cells by the ACTUAL header
      // x's (nearest column at/left of the cell) rather than assuming an order.
      // A PDF without those columns leaves them -1 → parse degrades to
      // "everything after the tracker id = comment".
      let xTracker = -1, xDesc = -1, xModule = -1, xForm = -1;
      for (let h = 0; h < pageLines.length; h++) {
        const low = pageLines[h]!.map(c => c.str).join(' ').toLowerCase();
        if (/tracker\s*id/.test(low) && /description/.test(low)) {
          // A column label can wrap onto an adjacent line ("MODULE" above /
          // "NAME" below), so pdf.js reports it on its OWN y — not the row that
          // carries "Tracker ID"/"Description". Scan a small window around the
          // header row so every column x is still learned (else the Module
          // column x stays -1 and its cells get dropped — 2026-10-29 fix).
          for (let k = Math.max(0, h - 2); k <= Math.min(pageLines.length - 1, h + 2); k++) {
            for (const c of pageLines[k]!) {
              const s = c.str.toLowerCase();
              if (xTracker < 0 && s.startsWith('tracker')) xTracker = c.x;
              if (xDesc    < 0 && s.startsWith('descrip')) xDesc    = c.x;
              if (xModule  < 0 && s.startsWith('module'))  xModule  = c.x;
              if (xForm    < 0 && s.startsWith('form'))    xForm    = c.x;
            }
          }
          if (xDesc > 0) break;
        }
      }
      const hasCols = xDesc > 0;
      // Data-column anchors sorted by x (whatever order this PDF used). A cell is
      // assigned to the column whose midpoint boundary it has crossed — so the
      // parse works whether Description is first (old layout) or last (new).
      const anchors = ([
        xModule > 0 ? { x: xModule, col: 'module' as const } : null,
        xForm   > 0 ? { x: xForm,   col: 'form'   as const } : null,
        xDesc   > 0 ? { x: xDesc,   col: 'desc'   as const } : null,
      ].filter(Boolean) as { x: number; col: 'module' | 'form' | 'desc' }[])
        .sort((a, b) => a.x - b.x);
      const bounds: number[] = [];
      for (let i = 0; i < anchors.length - 1; i++) bounds.push((anchors[i]!.x + anchors[i + 1]!.x) / 2);
      // Left edge of the first DATA column — cells left of it are No. / Tracker.
      const leftBound = anchors.length === 0 ? Infinity
        : xTracker > 0 ? (xTracker + anchors[0]!.x) / 2
        : anchors[0]!.x - 20;
      const bucketCell = (x: number): 'module' | 'form' | 'desc' | null => {
        if (x < leftBound) return null;
        let i = 0;
        while (i < bounds.length && x >= bounds[i]!) i++;
        return anchors[i]!.col;
      };

      const parsedPosts: VersionPostInput[] = [];
      const seen = new Set<string>();
      let currentCategory = 0;
      let cur: { ticket: string; desc: string; module: string; form: string } | null = null;

      const flushPending = () => {
        if (cur && cur.ticket && !seen.has(cur.ticket)) {
          seen.add(cur.ticket);
          parsedPosts.push({
            ticketId: cur.ticket,
            category: currentCategory,
            comment: cur.desc.replace(/\s+/g, ' ').trim().slice(0, 500),
            subject: '',
            moduleName: cur.module.replace(/\s+/g, ' ').trim(),
            formName: cur.form.replace(/\s+/g, ' ').trim(),
          });
        }
        cur = null;
      };

      // Version Summary header fields parsed from the PDF (applied to the form
      // after the loop): Version Number / Version Ticket ID / Released By / Date
      // Released. The BTSOFT CRR template lays the summary out as a 2-column
      // table with NO colon, and pdf.js reports each label and its value on
      // SEPARATE lines (the value baseline rounds a unit below the label's) — so
      // read the value from the rest of the label's line, else from the NEXT
      // line. The older "Label: value" single-line format still works.
      // (2026-10-29 fix: colon-only same-line matching captured nothing.)
      const header: Partial<VersionLogInput> = {};
      const lineText = pageLines.map(cells => cells.map(c => c.str).join(' '));
      const headerValue = (i: number, rest: string): string => {
        const sameLine = rest.replace(/^[:\s]+/, '').trim();
        if (sameLine) return sameLine;
        const next = (lineText[i + 1] ?? '').trim();
        return /^[:\s]*$/.test(next) ? '' : next;   // blank next line = no value
      };
      for (let i = 0; i < lineText.length; i++) {
        const ln = lineText[i]!;
        if (/version\s*summary/i.test(ln)) continue;   // section heading, not a field
        let hm: RegExpMatchArray | null;
        // Most specific first: "Version Tracker ID" must win over "Version".
        if ((hm = ln.match(/version\s*tracker\s*id\b(.*)$/i))) { const v = headerValue(i, hm[1]!); if (v) header.ticketId = v.replace(/\s+/g, ''); continue; }
        if ((hm = ln.match(/released\s*by\b(.*)$/i)))          { const v = headerValue(i, hm[1]!); if (v) header.releasedBy = v.replace(/\s+/g, ' ').trim(); continue; }
        if ((hm = ln.match(/release\s*date\b(.*)$/i)))         { const v = headerValue(i, hm[1]!); const iso = pdfDateToIso(v); if (iso) header.dateReleased = iso; continue; }
        if ((hm = ln.match(/^\s*version\b(.*)$/i)))            { const v = headerValue(i, hm[1]!); if (v) header.versionNumber = v.replace(/\s+/g, ''); continue; }
      }

      for (const cells of pageLines) {
        const line = cells.map(c => c.str).join(' ');
        const low = line.toLowerCase();

        // Section headings set the category of the posts that follow. "Bugs
        // Fixed" needs the optional "s" + loose spacing, else it stays the
        // default New Feature (2026-10-29 fix).
        if (/bugs?\s*fix/.test(low) && !/tracker/.test(low)) { flushPending(); currentCategory = 2; continue; }
        if (/enhancement/.test(low) && !/tracker/.test(low)) { flushPending(); currentCategory = 1; continue; }
        if (/new\s*feature/.test(low) && !/tracker/.test(low)) { flushPending(); currentCategory = 0; continue; }

        // (Version Summary header fields are parsed in the dedicated pass above,
        //  not here — the label and value sit on separate lines with no colon.)

        // Posts-table header row ("No. | Tracker ID | Description | …")
        if (/tracker\s*id/.test(low)) continue;

        // Data row: "<No.> <TrackerID> …". Pull the tracker id, then bucket the
        // rest of the cells into Description / Module Name / Form Name by column x.
        const rowMatch = line.match(/^\s*(\d{1,2})\s+(\d{3,6})\b(.*)$/);
        if (rowMatch) {
          flushPending();
          const ticket = rowMatch[2] ?? '';
          if (hasCols) {
            const b = { desc: '', module: '', form: '' };
            for (const c of cells) {
              const col = bucketCell(c.x);                // null = No. / Tracker
              if (!col) continue;
              b[col] += (b[col] ? ' ' : '') + c.str;
            }
            cur = { ticket, desc: b.desc, module: b.module, form: b.form };
          } else {
            cur = { ticket, desc: (rowMatch[3] ?? '').trim(), module: '', form: '' };
          }
          continue;
        }

        // Page footer / boilerplate that sits between or after the table rows
        // (e.g. "Version 26.10.7 | Released on 10/26/2026", "Page 1 of 2") —
        // close any in-progress row and NEVER append it to a column, otherwise
        // it leaks into the last row's Module / Description.
        if ((/version\s+[\d.]+/i.test(line) && /released\s+on/i.test(line))
            || /^\s*released\s+on\b/i.test(line)
            || /^\s*page\s+\d+\s+of\s+\d+\s*$/i.test(line)
            // CRR footer "<doc no> · Page 1 of 1" — "Page N of M" at the line
            // end (not just the line start), so it never leaks into the last
            // row's Description (2026-10-29 fix).
            || /\bpage\s+\d+\s+of\s+\d+\s*$/i.test(line)) {
          flushPending();
          continue;
        }

        // Continuation line (wrapped text) — append each cell to its column.
        if (cur) {
          if (hasCols) {
            for (const c of cells) {
              const col = bucketCell(c.x);
              if (!col || /^\d{1,2}$/.test(c.str)) continue;
              cur[col] += (cur[col] ? ' ' : '') + c.str;
            }
          } else {
            const t = line.trim();
            if (t && !/^\s*\d{1,2}\s*$/.test(t)) cur.desc += ' ' + t;
          }
        }
      }
      flushPending();

      // Fill the Version Summary header fields (Version Number / Version Ticket
      // ID / Released By / Date Released). The PDF value wins only where present,
      // so a manual edit is never wiped by a field the PDF did not carry.
      const filledHeader = !!(header.versionNumber || header.ticketId || header.releasedBy || header.dateReleased);
      if (filledHeader) {
        setForm(f => ({
          ...f,
          versionNumber: header.versionNumber ?? f.versionNumber,
          ticketId:      header.ticketId      ?? f.ticketId,
          releasedBy:    header.releasedBy    ?? f.releasedBy,
          dateReleased:  header.dateReleased  ?? f.dateReleased,
        }));
      }

      if (!filledHeader && parsedPosts.length === 0) {
        notify.warning('No details found in the PDF. Check the PDF format.');
        return;
      }

      // Hold the PDF — it uploads on Save (after the record exists), so Browse
      // works on a NEW record too (the upload endpoint needs a saved record id).
      setPendingFile(file);

      if (parsedPosts.length > 0) {
        const newPostItems: VersionPostItem[] = parsedPosts.map((p, i) => ({
          oid: `__pending__${Date.now()}_${i}`,
          ticketId: p.ticketId,
          category: p.category,
          categoryLabel: CATEGORY_OPTIONS.find(o => o.value === p.category)?.label ?? '',
          comment: p.comment,
          subject: p.subject,
          moduleName: p.moduleName,
          formName: p.formName,
          author: 'Service',
          postedDateTime: null,
        }));
        setDetail(prev => prev ? { ...prev, posts: [...(prev.posts ?? []), ...newPostItems] } : prev);
      }

      const loadedParts: string[] = [];
      if (filledHeader) loadedParts.push('version details');
      if (parsedPosts.length > 0) loadedParts.push(`${parsedPosts.length} post(s)`);
      notify.success(`Loaded from PDF: ${loadedParts.join(' + ')}. Click Save to persist.`);
    } catch (err) {
      console.error('PDF parse error:', err);
      notify.error('Failed to parse PDF. Please check the file and try again.');
    } finally {
      setPdfParsing(false);
    }
  };

  // mutations
  const createMut = useMutation({ mutationFn: (inp: VersionLogInput) => vlApi.create(inp) });
  const updateMut = useMutation({ mutationFn: ({ id, inp }: { id: string; inp: VersionLogInput }) => vlApi.update(id, inp) });
  const deleteMut = useMutation({ mutationFn: (id: string) => vlApi.delete(id) });
  const delPostMut = useMutation({ mutationFn: ({ id, postId }: { id: string; postId: string }) => vlApi.deletePost(id, postId) });

  const busy = createMut.isPending || updateMut.isPending;

  // ── Detail open / close / navigate ──────────────────────────────────────────

  const openNew = () => {
    if (!canWrite) { notify.warning('Only the Service user can change the Version Log.'); return; }
    setEditId(null);
    setDetail(EMPTY_DETAIL);
    // G5 AfterConstruction: a new version is dated today (the server clock in G5; the browser's day here).
    const t = new Date();
    const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    setForm({ ...EMPTY_INPUT, dateReleased: today });
    setErrors({});
    setAttachFileName('');
    setPendingFile(null);
    setMode('new');
  };

  /** Load a record and show its detail in the given mode ('view' = read-only). */
  const openRecord = async (id: string | null, m: 'view' | 'edit') => {
    if (!id) return;
    try {
      const res = await vlApi.getById(id);
      const d = res.data.data;
      setDetail(d);
      setEditId(id);
      setForm({
        versionNumber:    d.versionNumber ?? '',
        xafVersionNumber: d.xafVersionNumber ?? '',
        description:      d.description ?? '',
        dateReleased:     d.dateReleased ? d.dateReleased.substring(0, 10) : '',
        releasedBy:       d.releasedBy ?? '',
        dateTested:       d.dateTested ? d.dateTested.substring(0, 10) : '',
        testedBy:         d.testedBy ?? '',
        ticketId:         d.ticketId ?? '',
      });
      setErrors({});
      if (d.attachment) {
        const parts = d.attachment.split('/');
        setAttachFileName(parts[parts.length - 1] ?? d.attachment);
      } else {
        setAttachFileName('');
      }
      setPendingFile(null);
      setMode(canWrite ? m : 'view');
    } catch {
      notify.error('Failed to load version log detail.');
    }
  };

  const backToList = () => {
    setMode('list');
    setDetail(null);
    setEditId(null);
    setAttachFileName('');
    setPendingFile(null);
    setSelectedIds(new Set());
  };

  /** List View / Detail View switch: Detail opens the selected (or first) row. */
  const toggleView = (v: 'list' | 'detail') => {
    if (v === 'list') { backToList(); return; }
    const id = effectiveSelectedId ?? filteredRows[0]?.oid ?? null;
    if (id) void openRecord(id, 'view');
  };

  const validate = (): boolean => {
    const e: Partial<VersionLogInput> = {};
    // G5's wording (VersionControl.cs [RuleRequiredField] messages).
    if (!form.versionNumber.trim()) e.versionNumber = 'Version Number must not to be empty';
    if (!form.ticketId.trim())      e.ticketId      = "'Ticket ID' must not be empty.";
    if (!form.description.trim())   e.description   = 'Description must not to be empty';
    if (!form.releasedBy.trim())    e.releasedBy    = "'Released By' must not be empty.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isNew = mode === 'new';

  /** Save the header (+ any pending PDF-parsed posts). `after` = where to land. */
  const submit = (after: 'stay' | 'new' | 'close') => {
    if (!validate()) return;
    // The shared useSaveFlow does not surface the handler's return value, so the
    // new record's id is captured in this closure and read back in onSaved.
    let savedId: string | null = editId;
    runSave({
      confirmTitle: isNew ? 'Save' : 'Update',
      confirmMessage: isNew ? 'Do you want to Save?' : 'Do you want to Update?',
      save: async () => {
        if (!isNew && editId) {
          await updateMut.mutateAsync({ id: editId, inp: form });
        } else {
          const res = await createMut.mutateAsync(form);
          savedId = res.data.data.id;
        }
        if (savedId) {
          // Upload the browsed PDF now that the record exists (New or Edit).
          if (pendingFile) {
            try { await vlApi.uploadAttachment(savedId, pendingFile); } catch { /* non-fatal */ }
          }
          const pending = (detail?.posts ?? []).filter(p => p.oid.startsWith('__pending__'));
          for (const p of pending) {
            try {
              await vlApi.addPost(savedId, {
                ticketId: p.ticketId ?? '', category: p.category,
                comment: p.comment ?? '', subject: p.subject ?? '',
                moduleName: p.moduleName ?? '', formName: p.formName ?? '',
              });
            } catch { /* skip individual failures */ }
          }
        }
        qc.invalidateQueries({ queryKey: ['version-log'] });
      },
      successMessage: isNew
        ? `Version ${form.versionNumber} saved successfully.`
        : `Version ${form.versionNumber} updated successfully.`,
      onSaved: () => {
        if (after === 'close') { backToList(); return; }
        if (after === 'new')   { openNew(); return; }
        // stay → reopen the just-saved record in read-only VIEW mode (standard)
        if (savedId) void openRecord(savedId, 'view');
      },
      onError: (e: unknown) => notify.error(serverMessage(e) ?? (e as { message?: string })?.message ?? 'Save failed.'),
      formName: 'Version Log',
      auditAction: isNew ? 'create' : 'edit',
    });
  };

  // ── List Delete ─────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!canDelete) return;
    const idsToDelete = [...selectedIds] as string[];
    if (idsToDelete.length === 0) return;

    const label = idsToDelete.length === 1
      ? `version "${rows.find(r => r.oid === idsToDelete[0])?.versionNumber ?? idsToDelete[0]}"`
      : `${idsToDelete.length} selected version(s)`;

    await runDelete({
      formName: 'Version Log',
      message: `Delete ${label}?`,
      delete: async () => {
        let failed = 0;
        for (const id of idsToDelete) {
          try { await deleteMut.mutateAsync(id); } catch { failed++; }
        }
        if (failed > 0) throw new Error(`${failed} of ${idsToDelete.length} failed to delete.`);
      },
      successMessage: idsToDelete.length === 1 ? 'Deleted.' : `${idsToDelete.length} versions deleted.`,
      onDeleted: () => { qc.invalidateQueries({ queryKey: ['version-log'] }); setSelectedIds(new Set()); },
    });
  };

  // ── Posts ─────────────────────────────────────────────────────────────────

  const handleAddPost = () => {
    // Buffer the post as a PENDING grid row — it persists on the MAIN Save (like
    // the PDF-parsed posts), so "Add Post" never commits to the DB on its own.
    // Previously this called the API immediately, so a post saved even when the
    // user later clicked Cancel (user report 2026-10-28).
    if (!postForm.ticketId.trim() && !postForm.comment.trim()) {
      notify.warning('Enter a Task Ticket ID or Comment for the post.');
      return;
    }
    const newItem: VersionPostItem = {
      oid: `__pending__${Date.now()}`,
      ticketId: postForm.ticketId,
      category: postForm.category,
      categoryLabel: CATEGORY_OPTIONS.find(o => o.value === postForm.category)?.label ?? '',
      comment: postForm.comment,
      subject: postForm.subject,
      moduleName: postForm.moduleName,
      formName: postForm.formName,
      author: 'Service',
      postedDateTime: null,
    };
    setDetail(prev => prev ? { ...prev, posts: [...(prev.posts ?? []), newItem] } : prev);
    setPostDlgOpen(false);
    setPostForm(EMPTY_POST);
  };

  const handleDeletePost = async (postId: string) => {
    if (!editId) return;
    await runDelete({
      message: 'Remove this post?',
      delete: async () => { await delPostMut.mutateAsync({ id: editId, postId }); },
      successMessage: null,
      onDeleted: async () => { const res = await vlApi.getById(editId); setDetail(res.data.data); },
      onError: () => notify.error('Failed to remove post.'),
    });
  };

  // ── Attachment download (list + detail) ─────────────────────────────────────
  const handleDownload = async (e: React.MouseEvent | null, id: string, fileHint?: string | null) => {
    e?.stopPropagation();
    try {
      const res = await vlApi.downloadAttachment(id);
      const fileName = (fileHint ?? '').split('/').pop() || `${id}.pdf`;
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Failed to download attachment.');
    }
  };

  // ── Export Version Log Query (G5 ExportVersionLogQuery) ─────────────────────
  const exportScript = async () => {
    // G5 refuses an unsaved version: the script is built from the stored record.
    if (!editId) { notify.error('Save the data before exporting the query.'); return; }
    if (!form.ticketId.trim()) { notify.error('TicketID cannot be empty.'); return; }
    try {
      const res = await api.get(`/api/v1/version-log/${editId}/export-script`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Ticket_${form.ticketId.trim()}.txt`; a.click();
      URL.revokeObjectURL(url);
      notify.success('Exported successfully.');
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Failed to export the query.');
    }
  };

  // ── Version Tracking (cross-version Posts report) ───────────────────────────
  const openTracking = async () => {
    setModuleFilter([]); setFormFilter([]);
    setTrackingOpen(true);
    setTrackingLoading(true);
    try {
      const res = await vlApi.tracking();
      setTrackingRows(res.data.data ?? []);
    } catch {
      notify.error('Failed to load version tracking.');
    } finally {
      setTrackingLoading(false);
    }
  };
  const trackingModules = useMemo(
    () => [...new Set(trackingRows.map(r => (r.moduleName ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [trackingRows]);
  const trackingForms = useMemo(() => {
    // Cascade: when modules are picked, offer only the forms under them.
    const base = moduleFilter.length
      ? trackingRows.filter(r => moduleFilter.includes((r.moduleName ?? '').trim()))
      : trackingRows;
    return [...new Set(base.map(r => (r.formName ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [trackingRows, moduleFilter]);
  const trackingFiltered = useMemo(() => trackingRows.filter(r =>
    (moduleFilter.length === 0 || moduleFilter.includes((r.moduleName ?? '').trim())) &&
    (formFilter.length   === 0 || formFilter.includes((r.formName ?? '').trim()))
  ), [trackingRows, moduleFilter, formFilter]);
  const trackingColumns: AppColumn<VersionTrackingItem>[] = [
    { field: 'versionNumber', header: 'Version', width: 90 },
    { field: 'dateReleased', header: 'Date Released', type: 'date', width: 120 },
    { field: 'taskTicketId', header: 'Task Ticket ID', width: 120 },
    { field: 'category', header: 'Category', width: 110,
      value: r => r.categoryLabel ?? CATEGORY_OPTIONS.find(o => o.value === r.category)?.label ?? String(r.category),
      text:  r => r.categoryLabel ?? CATEGORY_OPTIONS.find(o => o.value === r.category)?.label ?? String(r.category) },
    { field: 'moduleName', header: 'Module Name', width: 160 },
    { field: 'formName', header: 'Form Name', width: 160 },
    { field: 'comment', header: 'Comment' },
  ];

  // ── Shared grid columns (also drive the shared "Export to") ────────────────
  const columns: AppColumn<VersionLogListItem>[] = [
    { field: 'versionNumber', header: 'Version Number' },
    {
      field: 'attachment', header: 'Version File', width: 100, align: 'center',
      sortable: false, filterable: false, noExport: true,
      render: (r) => r.attachment ? (
        <button className="vl-ver-link" title="Download PDF" onClick={(e) => handleDownload(e, r.oid, r.attachment)}>
          <i className="ti ti-file-type-pdf" style={{ fontSize: 18, color: '#c0392b' }} />
        </button>
      ) : null,
    },
    { field: 'dateReleased', header: 'Date Released', type: 'date' },
    { field: 'releasedBy', header: 'Released By' },
    { field: 'dateTested', header: 'Date Tested', type: 'date' },
    { field: 'testedBy', header: 'Tested By' },
    { field: 'ticketId', header: 'Version Ticket ID' },
  ];

  // Field helpers for the detail form.
  const bind = (key: keyof VersionLogInput) => ({
    value: form[key] as string,
    readOnly: viewOnly,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });
  const inputCls = (key: keyof VersionLogInput, required = false) => {
    if (viewOnly) return 'sd-input readonly';
    const empty = !((form[key] as string) ?? '').trim();
    return `sd-input${required && empty ? ' required-empty' : ''}`;
  };

  const postDlgKeys = useModalKeys(postDlgOpen, { onClose: () => setPostDlgOpen(false) });
  const trackingKeys = useModalKeys(trackingOpen, { onClose: () => setTrackingOpen(false) });

  // ── Detail action bar (top + bottom, identical) ────────────────────────────
  const actions = (where: 'top' | 'bottom') => (
    <div className={`sd-actionbar${where === 'bottom' ? ' bottom' : ''}`}>
      {viewOnly ? (<>
        {canWrite && (
          <button className="sd-abtn primary" disabled={busy}
            onClick={() => setMode('edit')}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
        )}
        {editId && (
          <button className="sd-abtn" disabled={busy} onClick={() => void exportScript()}
            title="Download the release script that re-creates this version and its posts on another database">
            <span className="tb-i tb-i-blue">⤓</span> Export Version Log Query
          </button>
        )}
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" disabled={busy} onClick={backToList}>
          <span className="tb-i">↩︎</span> Close
        </button>
      </>) : (<>
        <button className="sd-abtn primary" disabled={busy} onClick={() => submit('stay')}>
          <span className="tb-i">💾</span> Save
        </button>
        <button className="sd-abtn" disabled={busy} onClick={() => void exportScript()}
          title="Download the release script that re-creates this version and its posts on another database">
          <span className="tb-i tb-i-blue">⤓</span> Export Version Log Query
        </button>
        <button className="sd-abtn" disabled={busy} onClick={() => submit('close')}>
          <span className="tb-i">💾</span> Save and Close
        </button>
        <button className="sd-abtn" disabled={busy} onClick={() => submit('new')}>
          <span className="tb-i">📄</span> Save and New
        </button>
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" disabled={busy} onClick={backToList}>
          <span className="tb-i">↩︎</span> Cancel
        </button>
      </>)}
    </div>
  );

  // ── Detail render ───────────────────────────────────────────────────────────

  if (mode !== 'list') {
    const recName = isNew ? 'New' : (form.versionNumber || 'Version Log');
    return (
      <div className="bl-page">
        <div className="bl-grid-area">
          {/* The shared List View / Detail View switch (same component the
              reference form uses), above the detail card. */}
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="detail" onChange={toggleView} />
          </div>

          <div className="sd-wrap">
            {/* Header: title + breadcrumb, with prev/next record arrows at the right. */}
            <div className="sd-head">
              <div>
                <h2>Version Log – {recName}</h2>
                <div className="sd-bc">
                  <a onClick={backToList}>Home</a>
                  <i className="ti ti-chevron-right" />
                  <span>Maintenance</span>
                  <i className="ti ti-chevron-right" />
                  <span>Security</span>
                  <i className="ti ti-chevron-right" />
                  <a onClick={backToList}>Version Log</a>
                  <i className="ti ti-chevron-right" />
                  <span>{recName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record"
                  disabled={!prevId || busy}
                  onClick={() => prevId && void openRecord(prevId, viewOnly ? 'view' : 'edit')}>‹</button>
                <button className="sd-na next" title="Next record"
                  disabled={!nextId || busy}
                  onClick={() => nextId && void openRecord(nextId, viewOnly ? 'view' : 'edit')}>›</button>
              </div>
            </div>

            {actions('top')}

            <div className="sd-body">
              <div className="sd-section-h">GENERAL INFORMATION</div>

              {/* Header fields — XAF two-column, side-label layout */}
              <div className="vl-dgrid">
                {/* LEFT column */}
                <div className="vl-dcol">
                  <div className="vl-drow">
                    <label className="vl-dlabel">Version Number<span className="req">*</span></label>
                    <div className="vl-dfield">
                      <input className={inputCls('versionNumber', true)} {...bind('versionNumber')} placeholder="e.g. 26.10.7" />
                      {errors.versionNumber && <span className="sd-field-error">{errors.versionNumber}</span>}
                    </div>
                  </div>
                  <div className="vl-drow">
                    <label className="vl-dlabel">XAF Version Number</label>
                    <div className="vl-dfield"><input className={inputCls('xafVersionNumber')} {...bind('xafVersionNumber')} placeholder="e.g. 26.10.2.0" /></div>
                  </div>
                  <div className="vl-drow">
                    <label className="vl-dlabel">Version Ticket ID<span className="req">*</span></label>
                    <div className="vl-dfield">
                      <input className={inputCls('ticketId', true)} {...bind('ticketId')} placeholder="Ticket number" />
                      {errors.ticketId && <span className="sd-field-error">{errors.ticketId}</span>}
                    </div>
                  </div>
                  <div className="vl-drow vl-drow--tall">
                    <label className="vl-dlabel">Description<span className="req">*</span></label>
                    <div className="vl-dfield">
                      <textarea className={`${inputCls('description', true)} sd-input--tall`} rows={4}
                        {...bind('description')} placeholder="Release notes / description" />
                      {errors.description && <span className="sd-field-error">{errors.description}</span>}
                    </div>
                  </div>
                </div>

                {/* RIGHT column */}
                <div className="vl-dcol">
                  <div className="vl-drow">
                    <label className="vl-dlabel">Date Tested</label>
                    <div className="vl-dfield"><input type="date" className={inputCls('dateTested')} {...bind('dateTested')} /></div>
                  </div>
                  <div className="vl-drow">
                    <label className="vl-dlabel">Tested By</label>
                    <div className="vl-dfield"><input className={inputCls('testedBy')} {...bind('testedBy')} placeholder="Tester name" /></div>
                  </div>
                  <div className="vl-drow">
                    <label className="vl-dlabel">Date Released</label>
                    <div className="vl-dfield"><input type="date" className={inputCls('dateReleased')} {...bind('dateReleased')} /></div>
                  </div>
                  <div className="vl-drow">
                    <label className="vl-dlabel">Released By<span className="req">*</span></label>
                    <div className="vl-dfield">
                      {/* G5 ReleasedBy is the Deployment Team lookup. A value the list does not carry (a legacy row, or one
                          read from a PDF) stays pickable so opening and saving a record never loses it. */}
                      <AiCombo value={form.releasedBy || null} disabled={viewOnly} placeholder="N/A" erasable
                        options={[...(form.releasedBy && !teamNames.includes(form.releasedBy) ? [form.releasedBy] : []), ...teamNames]
                          .map(n => ({ value: n, label: n }))}
                        onChange={v => setForm(f => ({ ...f, releasedBy: v ?? '' }))} />
                      {errors.releasedBy && <span className="sd-field-error">{errors.releasedBy}</span>}
                    </div>
                  </div>
                  {/* Attachment (PDF) */}
                  <div className="vl-drow">
                    <label className="vl-dlabel">Attachment</label>
                    <div className="vl-dfield vl-attach-field">
                      <input type="text" readOnly className="sd-input readonly"
                        value={attachFileName || 'No file selected'} />
                      {attachFileName && editId && (
                        <button className="vl-ver-link" title="Download PDF"
                          onClick={() => handleDownload(null, editId, attachFileName)}>
                          <i className="ti ti-file-type-pdf" style={{ fontSize: 16, color: '#c0392b' }} />
                        </button>
                      )}
                      {!viewOnly && (
                        <button className="ca-btn" disabled={pdfParsing}
                          title="Browse a release PDF — its tickets load as Posts; the file uploads on Save"
                          onClick={() => fileInputRef.current?.click()}>
                          {pdfParsing
                            ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Parsing…</>
                            : <><span className="tb-i tb-i-blue">📎</span> Browse</>}
                        </button>
                      )}
                      <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) handlePdfFile(f); e.target.value = ''; }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Posts child grid */}
              <div className="vl-posts-section">
                <div className="vl-posts-hdr">
                  <span className="vl-posts-title">Posts</span>
                  {!viewOnly && (
                    <button className="ca-btn"
                      title="Add a post (saved when you click Save)"
                      onClick={() => { setPostForm(EMPTY_POST); setPostDlgOpen(true); }}>
                      <i className="ti ti-plus ai-circ ai-circ-add" aria-hidden="true" /> New
                    </button>
                  )}
                </div>
                <div className="vl-posts-table-wrap">
                  <table className="vl-posts-grid">
                    <thead>
                      <tr>
                        <th>Task Ticket ID</th>
                        <th>Category</th>
                        <th>Module Name</th>
                        <th>Form Name</th>
                        <th>Comment</th>
                        <th>Attachment</th>
                        <th>Author</th>
                        {!viewOnly && <th style={{ width: 40 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(!detail?.posts || detail.posts.length === 0) ? (
                        <tr><td colSpan={viewOnly ? 7 : 8} className="vl-posts-empty">No posts yet.</td></tr>
                      ) : detail.posts.map(p => (
                        <tr key={p.oid}>
                          <td>{p.ticketId}</td>
                          <td>{p.categoryLabel ?? CATEGORY_OPTIONS.find(o => o.value === p.category)?.label ?? p.category}</td>
                          <td>{p.moduleName}</td>
                          <td>{p.formName}</td>
                          <td>{p.comment}</td>
                          <td>N/A</td>
                          <td>{p.author ?? 'Service'}</td>
                          {!viewOnly && (
                            <td className="act">
                              <button className="vl-del-post" title="Remove post"
                                onClick={() => p.oid.startsWith('__pending__')
                                  ? setDetail(prev => prev ? { ...prev, posts: prev.posts.filter(x => x.oid !== p.oid) } : prev)
                                  : handleDeletePost(p.oid)}>
                                <i className="ti ti-trash" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Add-post dialog ── */}
        {postDlgOpen && (
          <div className="vl-overlay" style={{ zIndex: 1300 }} onMouseDown={() => setPostDlgOpen(false)}>
            <div className="vl-dialog" style={{ maxWidth: 480 }} onMouseDown={e => e.stopPropagation()} {...postDlgKeys}>
              <div className="vl-dialog-hdr">
                <i className="ti ti-message-plus" />
                <span>Add Post</span>
                <i className="ti ti-x vl-dialog-x" onClick={() => setPostDlgOpen(false)} />
              </div>
              <div className="vl-dialog-body">
                <div className="vl-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="vl-field">
                    <label>Task Ticket ID</label>
                    <input value={postForm.ticketId}
                      onChange={e => setPostForm(f => ({ ...f, ticketId: e.target.value }))} placeholder="Ticket number" />
                  </div>
                  <div className="vl-field">
                    <label>Category</label>
                    <AiCombo value={String(postForm.category)}
                      options={CATEGORY_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}
                      onChange={v => setPostForm(f => ({ ...f, category: Number(v ?? 0) }))} />
                  </div>
                  <div className="vl-field">
                    <label>Subject</label>
                    <input value={postForm.subject}
                      onChange={e => setPostForm(f => ({ ...f, subject: e.target.value }))} placeholder="Post subject" />
                  </div>
                  <div className="vl-field">
                    <label>Module Name</label>
                    <input value={postForm.moduleName}
                      onChange={e => setPostForm(f => ({ ...f, moduleName: e.target.value }))} placeholder="e.g. Sampling Management" />
                  </div>
                  <div className="vl-field">
                    <label>Form Name</label>
                    <input value={postForm.formName}
                      onChange={e => setPostForm(f => ({ ...f, formName: e.target.value }))} placeholder="e.g. Sample Registration" />
                  </div>
                  <div className="vl-field">
                    <label>Comment</label>
                    <textarea rows={4} value={postForm.comment}
                      onChange={e => setPostForm(f => ({ ...f, comment: e.target.value }))} placeholder="Post comment / details" />
                  </div>
                </div>
              </div>
              <div className="vl-dialog-footer">
                <button className="ca-btn" onClick={() => setPostDlgOpen(false)}>
                  <span className="tb-i tb-i-grey">↩︎</span> Cancel
                </button>
                <button className="ca-btn ca-btn-solid" onClick={handleAddPost}>
                  <i className="ti ti-plus ai-circ ai-circ-add" aria-hidden="true" /> Add Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="vl-page">
      <div className="vl-bc">
        <span>Home</span>
        <i className="ti ti-chevron-right" />
        <span>Maintenance</span>
        <i className="ti ti-chevron-right" />
        <span>Security</span>
        <i className="ti ti-chevron-right" />
        <span className="active">Version Log</span>
      </div>
      <FormTitle pageId="maint-versionlog" auditFormName="Version Log" loadMs={loadMs} loading={isFetching} />
      <div className="vl-sub">{filteredRows.length} item{filteredRows.length === 1 ? '' : 's'}</div>

      {/* The shared List View / Detail View switch (standing rule 10 landing = List). */}
      <div style={{ margin: '0 0 8px' }}>
        <ViewToggle view="list" onChange={toggleView} detailDisabled={filteredRows.length === 0} />
      </div>

      <div className="vl-body">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={openNew}
            newDisabled={!canWrite}
            newDisabledTitle="Only the Service user can change the Version Log."
            onEdit={() => openRecord(effectiveSelectedId, 'edit')}
            editDisabled={!canEdit || !canWrite}
            onDelete={handleDelete}
            deleteDisabled={!canDelete || !canWrite}
            deleteDisabledTitle={canWrite ? undefined : 'Only the Service user can change the Version Log.'}
            beforeRefresh={
              <button className="ca-btn" onClick={openTracking}
                title="Version Tracking — which versions / tasks touched a module or form">
                <span className="tb-i tb-i-blue">🔎</span> Version Tracking
              </button>
            }
            onRefresh={() => qc.invalidateQueries({ queryKey: ['version-log'] })}
            refreshing={isFetching}
            exportData={{ filename: 'Version Log', ...gridExportMatrix(visibleRows, columns) }}
          />
        </div>

        <GridSearch value={search} onChange={handleSearch} />

        <AppDataGrid<VersionLogListItem>
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.oid}
          loading={isFetching}
          emptyText={search ? 'No results match your search.' : 'No version log entries found.'}
          defaultPageSize={100}
          onVisibleRowsChange={setVisibleRows}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={(r) => setSelectedIds(prev => (prev.size === 1 && prev.has(r.oid)) ? new Set() : new Set([r.oid]))}
          onRowDoubleClick={(r) => openRecord(r.oid, 'view')}
        />
      </div>

      {/* ── Version Tracking dialog ── */}
      {trackingOpen && (
        <div className="vl-overlay" onMouseDown={() => setTrackingOpen(false)}>
          <div className="vl-dialog vl-track-dialog" onMouseDown={e => e.stopPropagation()} {...trackingKeys}>
            <div className="vl-dialog-hdr">
              <i className="ti ti-list-search" />
              <span>Version Tracking</span>
              <i className="ti ti-x vl-dialog-x" onClick={() => setTrackingOpen(false)} />
            </div>
            <div className="vl-dialog-body">
              <div className="vl-track-filters">
                <div className="vl-field">
                  <label>Module</label>
                  <CheckedCombo
                    options={trackingModules}
                    values={moduleFilter}
                    onChange={next => {
                      setModuleFilter(next);
                      // Drop any picked forms that no longer belong to the
                      // selected modules, so the two boxes stay consistent.
                      if (next.length) {
                        const allowed = new Set(
                          trackingRows
                            .filter(r => next.includes((r.moduleName ?? '').trim()))
                            .map(r => (r.formName ?? '').trim()));
                        setFormFilter(prev => prev.filter(f => allowed.has(f)));
                      }
                    }}
                    placeholder="All modules"
                    unit="modules"
                    ariaLabel="Filter by module"
                  />
                </div>
                <div className="vl-field">
                  <label>Form</label>
                  <CheckedCombo
                    options={trackingForms}
                    values={formFilter}
                    onChange={setFormFilter}
                    placeholder="All forms"
                    unit="forms"
                    ariaLabel="Filter by form"
                  />
                </div>
                <div className="vl-track-count">{trackingFiltered.length} row{trackingFiltered.length === 1 ? '' : 's'}</div>
              </div>
              <AppDataGrid<VersionTrackingItem>
                rows={trackingFiltered}
                columns={trackingColumns}
                getRowId={(r) => `${r.versionNumber ?? ''}|${r.taskTicketId ?? ''}|${r.moduleName ?? ''}|${r.formName ?? ''}`}
                loading={trackingLoading}
                defaultPageSize={50}
                emptyText={trackingRows.length === 0 ? 'No tracking data.' : 'No rows match the filter.'}
                exportFilename="Version Tracking"
                maximizable
              />
            </div>
            <div className="vl-dialog-footer">
              <button className="ca-btn" onClick={() => setTrackingOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
